import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Square, Bell, BellOff, ArrowUp, ArrowDown } from "lucide-react";
import { ElementLabel } from "./ElementLabel";
import { AlignmentComplete } from "./AlignmentComplete";
import IlluminatedBeing from "./IlluminatedBeing";
import { CHAKRAS } from "../data/chakras";
import { audioEngine } from "../lib/audioEngine";
import { useFocusStyle } from "../lib/focusScale";
import { useSavedState } from "../lib/savedState";

const BOX_OPTIONS = [4, 8, 12]; // seconds per side of the box breath
const boxPhases = (s) => [["Inhale", s], ["Hold", s], ["Exhale", s], ["Hold", s]];

const PRESETS = [
  { id: "solfeggio", label: "Pure Tone", engine: "solfeggio" },
  { id: "bowl", label: "Singing Bowl", engine: "bowl" },
  { id: "binaural", label: "Binaural Theta", engine: "binaural", beat: 6 },
];

const MANTRAS = [
  "Om Mane Padme Hum",
  "I Harmonize Body Mind and Spirit to the Sacred Trinity and Fundamental Five Elements of Creation",
  "Om Ma Na Pa Da Ma Om",
  "I Am that I Am that I Am that I Am that I Am",
  "From Telos to Alcyone I Align in the Radiant Oneness of Light and Life",
  "Ay Yum Hunab Ku Evam Maya Ay Ma Ho",
];

// ring expands from the Seed of Life center circle (r 34) to the Seed of Life outer edge (r 68)
const RING_MAX = (68 / 34) * 1.01;
const RING_MIN = 1.01; // mandala scales 1.01 while active

const OCTAVES = [
  { id: "low", label: "Low", mult: 0.5 },
  { id: "mid", label: "Mid", mult: 1 },
  { id: "high", label: "High", mult: 2 },
];

export default function HealingMode({ volume, focus, onEngage }) {
  const focusStyle = useFocusStyle(focus);
  const [preset, setPreset] = useSavedState("healing.sound", "binaural");
  const [octave, setOctave] = useSavedState("healing.octave", "low");
  const [boxSeconds, setBoxSeconds] = useSavedState("healing.seconds", 4);
  const [session, setSession] = useState(null); // null | "journey" | "manual"
  const [idx, setIdx] = useState(-1); // active chakra index, -1 = idle
  const [phase, setPhase] = useState("Breathe");
  const [scale, setScale] = useState(1);
  const [dur, setDur] = useState(4);
  const [geo, setGeo] = useState("seed");
  const [mantra, setMantra] = useState(MANTRAS[0]);
  const [afterglow, setAfterglow] = useState(false);

  const timer = useRef(null);
  const flowerTimer = useRef(null);
  const afterglowTimer = useRef(null);
  const mantraCounter = useRef(0);
  const boxRef = useRef(boxSeconds);
  const presetRef = useRef(preset);
  const octaveRef = useRef(octave);
  const [chimes, setChimes] = useSavedState("healing.chimes", true);
  const [direction, setDirection] = useSavedState("healing.direction", "up");
  const dirRef = useRef(direction);
  const chimesRef = useRef(chimes);

  useEffect(() => { boxRef.current = boxSeconds; }, [boxSeconds]);
  useEffect(() => { presetRef.current = preset; }, [preset]);
  useEffect(() => { octaveRef.current = octave; }, [octave]);
  useEffect(() => { chimesRef.current = chimes; }, [chimes]);

  const playing = session !== null;
  const activeChakra = idx >= 0 ? CHAKRAS[idx] : null;
  const octMult = (OCTAVES.find((o) => o.id === octave) || OCTAVES[1]).mult;
  const ringColor = activeChakra ? activeChakra.color : "#7c8cff";
  const ringGlow = activeChakra ? activeChakra.glow : "rgba(124,140,255,0.5)";

  const playTone = (freq) => {
    const cfg = PRESETS.find((p) => p.id === presetRef.current) || PRESETS[0];
    const mult = (OCTAVES.find((o) => o.id === octaveRef.current) || OCTAVES[1]).mult;
    audioEngine.setVolume(volume);
    audioEngine.play(freq * mult, { preset: cfg.engine, beat: cfg.beat });
  };

  const nextMantra = () => {
    setMantra(MANTRAS[mantraCounter.current % MANTRAS.length]);
    mantraCounter.current += 1;
  };

  // one full box-breath cycle for chakra `i`, then advance (journey) or stop (manual)
  const breathChime = (i) => {
    if (!chimesRef.current) return;
    const mult = (OCTAVES.find((o) => o.id === octaveRef.current) || OCTAVES[1]).mult;
    audioEngine.chime(CHAKRAS[i].frequency * mult * 2, 0.15);
  };

  const nextIndex = (i) => {
    const n = dirRef.current === "down" ? i - 1 : i + 1;
    return n >= 0 && n < CHAKRAS.length ? n : null;
  };

  const runCycle = (i, mode) => {
    const s = boxRef.current;
    setIdx(i);
    setSession(mode);
    setGeo("seed");
    nextMantra();
    playTone(CHAKRAS[i].frequency);

    const cycleMs = s * 4 * 1000;
    clearTimeout(flowerTimer.current);
    flowerTimer.current = setTimeout(() => setGeo("flower"), Math.max(300, cycleMs - 3000));

    const phases = boxPhases(s);
    let p = 0;
    const stepPhase = () => {
      const [label, seconds] = phases[p];
      setPhase(label);
      setDur(seconds);
      breathChime(i);
      if (label === "Inhale") setScale(RING_MAX);
      else if (label === "Exhale") setScale(RING_MIN);
      p += 1;
      if (p < phases.length) {
        timer.current = setTimeout(stepPhase, seconds * 1000);
      } else {
        timer.current = setTimeout(() => {
          if (!(mode === "journey" && nextIndex(i) !== null)) breathChime(i);
          onCycleDone(i, mode);
        }, seconds * 1000);
      }
    };
    stepPhase();
  };

  const onCycleDone = (i, mode) => {
    if (mode === "journey" && nextIndex(i) !== null) {
      runCycle(nextIndex(i), "journey");
    } else {
      if (mode === "journey") {
        audioEngine.chime(963);
        setAfterglow(true);
        clearTimeout(afterglowTimer.current);
        afterglowTimer.current = setTimeout(() => setAfterglow(false), 9000);
      }
      stopSession();
    }
  };

  const stopSession = () => {
    clearTimeout(timer.current);
    clearTimeout(flowerTimer.current);
    audioEngine.stop(0.6);
    setSession(null);
    setIdx(-1);
    setPhase("Breathe");
    setScale(1);
    setGeo("seed");
  };

  const beginJourney = () => {
    if (playing) {
      stopSession();
      return;
    }
    setAfterglow(false);
    clearTimeout(afterglowTimer.current);
    mantraCounter.current = 0;
    onEngage?.();
    dirRef.current = direction;
    runCycle(direction === "down" ? CHAKRAS.length - 1 : 0, "journey");
  };

  const handleOrbClick = (c) => {
    if (session === "journey") return; // ignore during auto journey
    const i = CHAKRAS.findIndex((x) => x.id === c.id);
    if (session === "manual" && idx === i) {
      stopSession(); // tap again to stop early
      return;
    }
    clearTimeout(timer.current);
    clearTimeout(flowerTimer.current);
    runCycle(i, "manual"); // one cycle for this chakra
  };

  useEffect(() => {
    if (playing && idx >= 0) {
      const cfg = PRESETS.find((p) => p.id === preset) || PRESETS[0];
      audioEngine.play(CHAKRAS[idx].frequency * octMult, { preset: cfg.engine, beat: cfg.beat });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset, octave]);

  useEffect(() => () => {
    clearTimeout(timer.current);
    clearTimeout(flowerTimer.current);
    clearTimeout(afterglowTimer.current);
    audioEngine.stop(0.4);
  }, []);

  return (
    <div className="relative flex flex-1 flex-col items-center">
      {/* Mobile cues: own row above the figure so they never cover the head */}
      <div className={`pointer-events-none absolute inset-x-0 z-10 h-[68px] flex-col items-center justify-center text-center sm:hidden ${focus ? "focus-cue-top flex" : "top-0 flex"}`} data-testid="healing-mobile-cues">
        {activeChakra && (
          <>
            <ElementLabel chakra={activeChakra} testid="healing-element-mobile" className="mb-1 text-[11px]" />
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-cyan-300/80">
              {activeChakra.english} · {Math.round(activeChakra.frequency * octMult)} Hz · {activeChakra.note}
            </p>
            <p className="mt-1 font-serif text-2xl tracking-widest text-white/90">{phase}</p>
          </>
        )}
      </div>
      <div className="relative -mb-6 mt-3 flex w-full flex-1 items-center justify-center sm:mb-0 sm:mt-0">
        <div className="relative" style={focusStyle}>
        {/* breathing ring */}
        <motion.div
          aria-hidden
          className="absolute rounded-full border pointer-events-none"
          style={{
            width: "min(45.88vw, 242.1px)",
            height: "min(45.88vw, 242.1px)",
            left: "50%",
            top: "50.05%", // same centre as the Seed of Life
            x: "-50%",
            y: "-50%",
            borderColor: ringColor,
            boxShadow: `0 0 60px ${ringGlow}, inset 0 0 60px ${ringGlow}`,
          }}
          animate={{ scale, opacity: playing ? 0.7 : 0.2 }}
          transition={{ duration: dur, ease: "easeInOut" }}
          data-testid="breathing-ring"
        />
        <IlluminatedBeing
          activeChakraId={activeChakra?.id}
          onChakraClick={handleOrbClick}
          breathScale={playing ? (scale > RING_MIN ? 1.36 : 0.84) : 1}
          auraColor={ringGlow}
          geometry={geo}
          bloom={geo === "flower"}
          afterglow={afterglow}
          healingProgress={playing ? idx : -1}
        />
        </div>
        {/* Left cues in the dark space, outside the aura sphere */}
        <div
          className={`absolute left-4 top-1/2 hidden -translate-y-1/2 text-left sm:left-12 lg:left-24 ${focus && !activeChakra ? "" : "sm:block"}`}
          data-testid="meditation-cues"
        >
          {activeChakra ? (
            <>
              <ElementLabel chakra={activeChakra} testid="healing-element" className="mb-2" />
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-cyan-300/80" data-testid="frequency-cue">
                {activeChakra.english}
              </p>
              <p className="mt-1 font-mono text-xs uppercase tracking-[0.3em] text-cyan-300/55">
                {Math.round(activeChakra.frequency * octMult)} Hz · Note {activeChakra.note}
              </p>
              <p className="mt-6 font-serif text-4xl tracking-widest text-white/90 lg:text-5xl" data-testid="breath-phase">
                {phase}
              </p>
              {focus && (
                <AnimatePresence mode="wait">
                  <motion.p
                    key={mantra}
                    data-testid="healing-focus-mantra-side"
                    className="mt-8 hidden max-w-[20rem] font-serif text-xl italic leading-relaxed text-slate-200 lg:block"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.8 }}
                  >
                    "{mantra}"
                  </motion.p>
                </AnimatePresence>
              )}
            </>
          ) : (
            <p className="hidden max-w-[12rem] font-mono text-[11px] uppercase leading-relaxed tracking-[0.3em] text-slate-500 lg:block">
              Tap a chakra for one breath, or Begin to flow through all seven
            </p>
          )}
        </div>
        {/* Mobile cues moved above the stage */}
        <AlignmentComplete show={afterglow} direction={dirRef.current} onDismiss={() => setAfterglow(false)} />
      </div>

      {/* mantra floats at the bottom in full-screen view */}
      {focus && (
        <div className="focus-under-figure pointer-events-none fixed inset-x-0 bottom-6 z-40 flex justify-center px-6 sm:bottom-10 lg:hidden">
          <AnimatePresence mode="wait">
            <motion.p
              key={mantra}
              data-testid="healing-focus-mantra"
              className="max-w-2xl text-center font-serif text-lg italic text-slate-100 sm:text-2xl"
              style={{ textShadow: "0 0 18px rgba(0,0,0,0.9), 0 0 6px rgba(0,0,0,0.8)" }}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.8 }}
            >
              "{mantra}"
            </motion.p>
          </AnimatePresence>
        </div>
      )}

      {/* dock */}
      <div className={`relative w-full max-w-3xl rounded-2xl border border-white/10 bg-slate-950/70 p-4 sm:p-6 backdrop-blur-xl ${focus ? "hidden" : ""}`}>
        {!activeChakra && (
          <p
            className="mb-3 sm:mb-4 text-center font-mono text-[11px] uppercase leading-relaxed tracking-[0.25em] text-slate-400 lg:hidden"
            data-testid="meditation-idle-hint-mobile"
          >
            Tap a chakra for one breath, or Begin to flow through all seven
          </p>
        )}
        <div className="mb-3 flex flex-wrap items-center justify-center gap-3 sm:mb-4">
          <div className="order-last flex gap-1 rounded-full border border-white/10 bg-white/5 p-1 sm:order-none sm:justify-self-end">
            {[
              { id: "up", label: "Ascend", Icon: ArrowUp },
              { id: "down", label: "Descend", Icon: ArrowDown },
            ].map(({ id, label, Icon }) => (
              <button
                key={id}
                data-testid={`healing-direction-${id}`}
                onClick={() => setDirection(id)}
                disabled={session === "journey"}
                className={`flex items-center gap-1 rounded-full px-3 py-1.5 text-xs transition-colors disabled:cursor-not-allowed ${direction === id ? "bg-white/15 text-white" : "text-slate-400 hover:text-white disabled:hover:text-slate-400"}`}
              >
                <Icon size={12} /> {label}
              </button>
            ))}
          </div>
          <button
            data-testid="meditation-play-toggle"
            onClick={beginJourney}
            className="flex items-center gap-2 rounded-full bg-gradient-to-r from-emerald-500 to-teal-400 px-6 py-2.5 text-sm font-medium text-white shadow-lg shadow-emerald-500/30 transition-transform hover:scale-105"
          >
            {playing ? <Square size={15} /> : <Play size={16} />}
            {session === "journey" ? "Stop" : playing ? "Stop" : "Begin"}
          </button>
          <button
            data-testid="breath-chimes-toggle"
            onClick={() => setChimes((c) => !c)}
            className={`flex items-center gap-1.5 justify-self-start rounded-full border border-white/10 px-3 py-2 text-xs sm:px-4 transition-colors ${chimes ? "bg-white/15 text-white" : "bg-white/5 text-slate-400 hover:text-white"}`}
          >
            {chimes ? <Bell size={14} /> : <BellOff size={14} />} <span className="hidden sm:inline">Breath Chimes</span><span className="sm:hidden">Chimes</span> {chimes ? "On" : "Off"}
          </button>
        </div>
        <p className="mb-3 sm:mb-4 text-center font-mono text-[11px] leading-relaxed text-slate-500">
          Cycle through an In Breath, a Hold, an Out Breath and a Hold to optimize ATP generation. Breathe smoothly through the nose and into the belly to connect Life and Life circuit consciously.
        </p>
        <AnimatePresence mode="wait">
          <motion.p
            key={mantra}
            data-testid="healing-mantra"
            className="mb-3 sm:mb-4 text-center font-serif text-base sm:text-lg italic text-slate-200"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.6 }}
          >
            "{mantra}"
          </motion.p>
        </AnimatePresence>
        <div className="mt-1 flex flex-wrap items-center justify-center gap-2 sm:gap-3">
          <div className="flex items-center gap-1 rounded-full border border-white/10 bg-white/5 p-1">
            <span className="px-2 text-[10px] uppercase tracking-widest text-slate-500">Box</span>
            {BOX_OPTIONS.map((s) => (
              <button
                key={s}
                data-testid={`box-seconds-${s}`}
                onClick={() => setBoxSeconds(s)}
                className={`rounded-full px-3 py-1.5 text-xs transition-colors ${boxSeconds === s ? "bg-white/15 text-white" : "text-slate-400 hover:text-white"}`}
              >
                {s}s
              </button>
            ))}
          </div>

          <div className="flex gap-1 rounded-full border border-white/10 bg-white/5 p-1">
            {PRESETS.map((p) => (
              <button
                key={p.id}
                data-testid={`sound-preset-${p.id}`}
                onClick={() => setPreset(p.id)}
                className={`rounded-full px-3 py-1.5 text-xs transition-colors ${preset === p.id ? "bg-white/15 text-white" : "text-slate-400 hover:text-white"}`}
              >
                {p.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-1 rounded-full border border-white/10 bg-white/5 p-1">
            <span className="px-2 text-[10px] uppercase tracking-widest text-slate-500">Octave</span>
            {OCTAVES.map((o) => (
              <button
                key={o.id}
                data-testid={`octave-${o.id}`}
                onClick={() => setOctave(o.id)}
                className={`rounded-full px-3 py-1.5 text-xs transition-colors ${octave === o.id ? "bg-white/15 text-white" : "text-slate-400 hover:text-white"}`}
              >
                {o.label}
              </button>
            ))}
          </div>

        </div>
      </div>
    </div>
  );
}
